import { useState } from 'react';
import { configFieldLabel, configChoiceLabel, configSectionLabel } from '../../lib/definitions/settings';
import { fieldDomId } from './schema';
import type { SchemaField } from './schema';


// First sentence of a schema description, capped ~90 characters — the
// short line shown under every field. The schema's own field descriptions
// are the only durable documentation this config actually has (a plain
// YAML comment in config.yaml does not survive a save: ConfigManager
// re-serializes the whole file with plain PyYAML on every write, which
// drops all comments — see the `regions` list section's own schema
// description for the durable copy of that exact warning). So the full
// text must always stay reachable somewhere on screen — it's just not
// allowed to be the whole page, hence the short line + toggle below rather
// than either always-full or hidden-entirely.
function shortDescription(desc: string): string {
  const periodIdx = desc.indexOf(". ");
  let short = periodIdx === -1 ? desc : desc.slice(0, periodIdx + 1);
  if (short.length > 90) short = `${short.slice(0, 90).trimEnd()}…`;
  return short;
}


// First sentence of a SECTION description, capped ~140 characters — the
// same short-line + reveal-button pattern used for field descriptions
// above (see shortDescription()/descBlock in FieldEditor), just with a
// longer cap since this renders as a single line at the top of a whole
// pane rather than packed into a ~320px field column. Without this, the
// section description (e.g. regions' multi-sentence warning) is the
// loudest thing on the page — the exact "wall of text" problem the field-
// level pattern already solves, just relocated from per-field to
// per-section.
function shortSectionDescription(desc: string): string {
  const periodIdx = desc.indexOf(". ");
  let short = periodIdx === -1 ? desc : desc.slice(0, periodIdx + 1);
  if (short.length > 140) short = `${short.slice(0, 140).trimEnd()}…`;
  return short;
}


// Section-level description, rendered at the top of a pane. Reuses the
// exact same disclosure affordance as field descriptions (the round "i"
// toggle button and its CSS) rather than a second, differently-styled
// one. The full text must stay reachable — for a real schema description
// (e.g. regions) it's the only durable documentation this config has,
// since a plain YAML comment doesn't survive a save (ConfigManager
// re-serializes config.yaml with plain PyYAML on every write, dropping
// comments) — so it's hidden behind a toggle, never dropped outright. If
// truncating would change nothing, there's nothing to hide, so it renders
// as a plain line with no pointless control.
export function PaneDescription({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  const short = shortSectionDescription(text);
  if (short === text) {
    return <p className="settings-pane-description">{text}</p>;
  }
  return (
    <>
      <span className="settings-field-desc-row">
        <span className="settings-pane-description">{short}</span>
        <button
          type="button"
          className="settings-field-desc-toggle"
          aria-label={`Show full description for ${label}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          i
        </button>
      </span>
      {open && <p className="settings-pane-description settings-field-desc-full">{text}</p>}
    </>
  );
}


export function FieldEditor({
  field,
  value,
  dirty,
  highlighted,
  onChange,
}: {
  field: SchemaField;
  value: unknown;
  dirty?: boolean;
  // Purely visual: marks this field as the active search result inside
  // whatever section/record is currently on screen. Adds a class to the
  // same element FieldEditor already renders, never a wrapping element.
  highlighted?: boolean;
  onChange: (v: unknown) => void;
}) {
  const label = field.path === "audit.recalculate_cooldown_s" ? "Recalculation cooldown (inactive)" : configFieldLabel(field.path);
  const description = field.path === "audit.recalculate_cooldown_s"
    ? "This value is retained from the former manual recalculation control. It is stored in configuration, but current report-refresh code does not read it or use it to schedule refreshes."
    : field.description;
  const highlightClass = highlighted ? " settings-field-search-hit" : "";
  const [descOpen, setDescOpen] = useState(false);

  // Short line + toggle-able full text, shared by every field type below —
  // see this file's shortDescription() comment for why the full text must
  // stay reachable rather than simply omitted.
  const descBlock = description ? (
    <>
      <span className="settings-field-desc-row">
        <span className="settings-field-desc">{shortDescription(description)}</span>
        <button
          type="button"
          className="settings-field-desc-toggle"
          aria-label={`Show full description for ${label}`}
          aria-expanded={descOpen}
          onClick={() => setDescOpen((o) => !o)}
        >
          i
        </button>
      </span>
      {descOpen && <span className="settings-field-desc settings-field-desc-full">{description}</span>}
    </>
  ) : null;

  if (field.secret_ref) {
    return (
      <label className={`settings-field${highlightClass}`}>
        <span>
          {label}
        </span>
        <input type="password" placeholder="•••••••• (leave blank to keep current value)" onChange={(e) => onChange(e.target.value)} />
        {descBlock}
        <span className="settings-secret-note">Secret — never sent to the browser; only sent if you type a new value.</span>
      </label>
    );
  }

  // A fixed, known set of values (e.g. transports' `kind`) — a <select>
  // over the schema's own literal option strings, stored value unchanged,
  // but rendered through configChoiceLabel (CONFIG_CHOICE_LABEL in
  // definitions.ts) so the operator sees a human label, not a raw key.
  if (field.choices && field.choices.length > 0) {
    return (
      <label className={`settings-field${highlightClass}`}>
        <span>
          {label}
        </span>
        <select value={value != null ? String(value) : ""} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled={value != null && value !== ""}>
            (choose one)
          </option>
          {field.choices.map((c) => (
            <option key={c} value={c}>
              {configChoiceLabel(c)}
            </option>
          ))}
        </select>
        {descBlock}
      </label>
    );
  }

  if (field.type === "bool") {
    return (
      <label className={`settings-field settings-field-span-full${highlightClass}`} style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        <span>
          {label}
        </span>
        {descBlock}
      </label>
    );
  }

  if (field.type === "int" || field.type === "float") {
    return (
      <label className={`settings-field${highlightClass}`}>
        <span>
          {label}
        </span>
        <input
          type="number"
          step={field.type === "float" ? "any" : 1}
          value={value as number | string}
          onChange={(e) => onChange(field.type === "int" ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
        />
        {descBlock}
      </label>
    );
  }

  // list[str] (found 2026-09-20): a per-item list editor. A stray non-array
  // value (a field already corrupted by an earlier bug, or a hand-edited
  // config) is shown as a single editable row rather than silently blanked
  // to an empty list — the point is to make bad data visible and fixable,
  // never to make it quietly vanish.
  if (field.type === "list[str]") {
    const arr = Array.isArray(value) ? (value as unknown[]).map((v) => String(v)) : value != null && value !== "" ? [String(value)] : [];
    function updateAt(i: number, v: string) {
      const next = [...arr];
      next[i] = v;
      onChange(next);
    }
    function removeAt(i: number) {
      onChange(arr.filter((_, idx) => idx !== i));
    }
    function add() {
      onChange([...arr, ""]);
    }
    return (
      <label className={`settings-field settings-field-list settings-field-span-full${highlightClass}`}>
        <span>
          {label}
        </span>
        <div className="settings-list-field">
          {arr.length === 0 && <span className="settings-field-desc">(empty list)</span>}
          {arr.map((v, i) => (
            <div key={i} className="settings-list-row">
              <input type="text" value={v} onChange={(e) => updateAt(i, e.target.value)} />
              <button type="button" className="settings-save-btn" onClick={() => removeAt(i)}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="settings-save-btn settings-list-add" onClick={add}>
            Add
          </button>
        </div>
        {descBlock}
      </label>
    );
  }

  return (
    <label className={`settings-field${highlightClass}`}>
      <span>
        {label}
      </span>
      <input type="text" value={value != null ? String(value) : ""} onChange={(e) => onChange(e.target.value)} />
      {descBlock}
    </label>
  );
}


export function ReferenceOnlySection({ sectionKey, note, fields, highlightPath }: { sectionKey: string; note: string; fields: SchemaField[]; highlightPath: string | null }) {
  return (
    <div className="settings-pane">
      <PaneDescription text={note} label={configSectionLabel(sectionKey)} />
      <div className="settings-fields-grid">
        {fields.map((f) => (
          <div key={f.path} id={fieldDomId(sectionKey, f.path)} className={`settings-field${highlightPath === f.path ? " settings-field-search-hit" : ""}`}>
            <span>
              {configFieldLabel(f.path)} <span className="settings-field-desc">({f.type ?? "value"})</span>
            </span>
            {f.description && <span className="settings-field-desc">{shortDescription(f.description)}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
